import * as THREE from "three";
import type { Board } from "../board.ts";
import type { WallSlot } from "./hex.ts";
import { expertWalls, methodologyWall, rosterWall } from "./walls.ts";
import { textPanel } from "./text-panel.ts";
import { createSprite } from "./sprite.ts";

// Mount a panel/sprite flat on a wall, facing the centre, at height `y`. The
// mesh keeps its local x (so callers can spread several across one wall).
export function onWall(
  mesh: THREE.Mesh,
  slot: WallSlot,
  y: number,
): THREE.Group {
  const g = new THREE.Group();
  g.position.set(slot.x, y, slot.z);
  g.lookAt(0, y, 0); // +Z faces the centre
  mesh.position.z = 0.3; // just off the wall, toward the room
  g.add(mesh);
  return g;
}

const chartImage = (id: string) => `/assets/charts/${id}.png`;

// Per-topic walls: the bar-chart image (top) + the publishable headline synthesis
// (below). Methodology board + the "Expertos consultados" roster image. All
// content is aggregate/publishable — never individual positions/names.
export function createBillboards(board: Board): THREE.Group {
  const group = new THREE.Group();
  group.name = "billboards";

  for (const w of expertWalls) {
    const topic = board.topics.find((t) => t.id === w.topicId);
    if (!topic) continue;

    // Stance bar-chart for the topic.
    const chart = createSprite(chartImage(w.topicId), 2.8);
    group.add(onWall(chart.mesh, w.slot, 4.0));

    // Title + headline synthesis below the chart.
    const cap = textPanel(
      {
        title: topic.emoji ? `${topic.emoji} ${topic.title}` : topic.title,
        body: topic.headline?.trim() || "Guía de expertos — en preparación",
      },
      6.4,
      2.0,
    );
    group.add(onWall(cap, w.slot, 1.5));
  }

  group.add(onWall(
    textPanel(
      {
        title: board.methodology.title || "Metodología",
        body: board.methodology.body[0] ?? "Cómo trabajamos con los expertos.",
        footer: "Abre Ayuda ❓ para leer más",
      },
      6,
      4,
    ),
    methodologyWall,
    3.1,
  ));

  // Experts roster as the rendered image (title + 29 names, two columns).
  const roster = createSprite(chartImage("expertos"), 4.2);
  group.add(onWall(roster.mesh, rosterWall, 3.1));

  return group;
}
